"use client";

import Image from "next/image";
import { useState } from "react";
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";

import type { HomeDictionary, ProductItem } from "@/content/home";
import type { Locale } from "@/lib/locales";

interface ProductGalleryProps {
  locale: Locale;
  copy: HomeDictionary["technology"];
}

const bioremProductByLocale: Record<Locale, ProductItem> = {
  es: {
    id: "biorem",
    name: "BioRem-2000M Oil Digester",
    tagline: "Agente de biorremediacion certificado para degradacion acelerada de hidrocarburos",
    image: "/images/biorem-preview.webp",
    detailImage: "/images/biorem.webp",
    description:
      "Solucion biotecnologica certificada para degradar hidrocarburos de forma acelerada en suelos, aguas subterraneas, tanques y escenarios de respuesta temprana.",
    applications: [
      { title: "Suelos y excavaciones", description: "Elimina contaminantes residuales en fosas, patios de tratamiento y suelos impactados con aplicacion rapida y altamente soluble." },
      { title: "Aguas subterraneas", description: "Disenado para inyeccion directa y sistemas pump-and-treat con distribucion homogenea del agente activo." },
      { title: "Limpieza de tanques", description: "Reduce tiempos de limpieza y disminuye la parada operativa en recipientes, tanques y embarcaciones." },
      { title: "Respuesta a emergencias", description: "Disminuye la volatilizacion y niveles LEL para mitigar riesgos de incendio en derrames y contingencias." },
    ],
    mechanism: [
      { title: "Biodispersion", description: "Dispersa los hidrocarburos desde conglomerados macroscopicos hacia microgotas mas tratables." },
      { title: "Solubilizacion", description: "Convierte compuestos hidrofobicos en formas mas compatibles con el transporte celular y la accion microbiana." },
      { title: "Asimilacion", description: "Favorece la ruptura enzimatica de cadenas largas de hidrocarburos para acelerar su consumo biologico." },
      { title: "Mineralizacion", description: "Promueve la conversion final en dioxido de carbono y agua como parte del proceso metabolico." },
    ],
    properties: [
      { label: "Estado fisico", value: "Liquido tipo agua" },
      { label: "Punto de ebullicion", value: "100 C / 212 F" },
      { label: "Punto de inflamacion", value: "No inflamable" },
      { label: "Gravedad especifica", value: "1.08" },
      { label: "Solubilidad", value: "Completamente soluble" },
    ],
  },
  en: {
    id: "biorem",
    name: "BioRem-2000M Oil Digester",
    tagline: "Certified bioremediation agent for accelerated hydrocarbon degradation",
    image: "/images/biorem-preview.webp",
    detailImage: "/images/biorem.webp",
    description:
      "Certified biotechnological solution for accelerated hydrocarbon degradation across soil, groundwater, tank cleaning, and emergency response scenarios.",
    applications: [
      { title: "Soil and excavation", description: "Eliminates residual contaminants in pits, treatment yards, and impacted soils with fast, highly soluble application." },
      { title: "Groundwater", description: "Designed for direct injection and pump-and-treat systems with uniform active-agent distribution." },
      { title: "Tank cleaning", description: "Reduces cleaning time and lowers downtime in tanks, vessels, and process containers." },
      { title: "Emergency response", description: "Rapidly lowers volatilization and LEL levels to mitigate fire hazards during spills and contingencies." },
    ],
    mechanism: [
      { title: "Bio-dispersion", description: "Disperses hydrocarbons from macroscopic clusters into smaller droplets that are easier to treat." },
      { title: "Solubilization", description: "Transforms hydrophobic compounds into forms more compatible with cellular transport and microbial action." },
      { title: "Assimilation", description: "Supports enzymatic breakdown of long-chain hydrocarbons to accelerate biological consumption." },
      { title: "Mineralization", description: "Drives final conversion into carbon dioxide and water as part of the metabolic process." },
    ],
    properties: [
      { label: "Physical state", value: "Water-like liquid" },
      { label: "Boiling point", value: "100 C / 212 F" },
      { label: "Flash point", value: "Non-flammable" },
      { label: "Specific gravity", value: "1.08" },
      { label: "Solubility", value: "Completely soluble" },
    ],
  },
};

export default function ProductGallery({ locale, copy }: ProductGalleryProps) {
  const [selectedProduct, setSelectedProduct] = useState<ProductItem | null>(null);
  const [infoTab, setInfoTab] = useState<"applications" | "mechanism" | "specs">("applications");
  const shouldReduceMotion = useReducedMotion();
  const viewport = { once: true, amount: 0.2 };
  const products = [bioremProductByLocale[locale], ...copy.products.filter((product) => product.id === "pcp-pumps")];

  return (
    <LazyMotion features={domAnimation}>
      <section id="tecnologia" className="relative py-32 px-6 lg:px-12 overflow-hidden bg-[#0A111F]">
        <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-20 grayscale bg-[url('/images/texture.webp')]" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_center,_transparent_0%,_#0A111F_90%)]" />

        <div className="max-w-[1400px] mx-auto relative z-20">
          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewport}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8 relative z-30"
          >
            <div className="max-w-2xl">
              <span className="text-terracotta font-bold text-[10px] uppercase tracking-[0.3em] mb-4 block font-montserrat">
                {copy.eyebrow}
              </span>
              <AnimatePresence mode="wait" initial={false}>
                <m.h2
                  key={selectedProduct ? "sheet" : "catalog"}
                  initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -18 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="font-montserrat font-black text-5xl md:text-7xl text-white uppercase leading-[0.85] tracking-tighter"
                >
                  {selectedProduct ? (
                    copy.sheetTitle
                  ) : (
                    <>
                      {copy.title} <span className="text-terracotta">{copy.accent}</span>
                    </>
                  )}
                </m.h2>
              </AnimatePresence>
            </div>

            <div className="z-10">
              <AnimatePresence>
                {selectedProduct && (
                  <m.button
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, x: 18 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, x: 18 }}
                    transition={{ duration: 0.25 }}
                    onClick={() => setSelectedProduct(null)}
                    className="px-8 py-4 bg-terracotta text-white font-bold text-[10px] uppercase tracking-widest hover:bg-white hover:text-petroleum transition-all shadow-xl flex items-center gap-3"
                  >
                    <span>&larr;</span> {copy.backToCatalog}
                  </m.button>
                )}
              </AnimatePresence>
            </div>
          </m.div>

          <AnimatePresence mode="wait">
            {!selectedProduct ? (
              <m.div
                key="catalog"
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -24 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-30"
              >
                {products.map((product, index) => (
                  <m.div
                    key={product.id}
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={viewport}
                    transition={{ duration: 0.5, delay: shouldReduceMotion ? 0 : index * 0.08 }}
                    className="bg-white/[0.03] backdrop-blur-md border border-white/10 p-8 flex flex-col group hover:border-terracotta/40 hover:bg-white/[0.06] transition-all duration-500"
                  >
                    <div className="aspect-square bg-white/5 mb-8 overflow-hidden relative border border-white/5">
                      <Image
                        src={product.image}
                        alt={product.name}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                        quality={60}
                        className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-petroleum/20 group-hover:bg-transparent transition-colors z-10" />
                    </div>
                    <h3 className="text-2xl font-black font-montserrat uppercase mb-4 text-white group-hover:text-terracotta transition-colors leading-tight">
                      {product.name}
                    </h3>
                    <p className="text-sm text-white/60 mb-8 flex-grow leading-relaxed font-light">
                      {product.description}
                    </p>
                    <button
                      onClick={() => {
                        setSelectedProduct(product);
                        setInfoTab("applications");
                      }}
                      className="w-full h-14 bg-transparent border border-white/20 text-white font-bold text-[10px] uppercase tracking-widest hover:bg-terracotta hover:border-terracotta transition-all"
                    >
                      {copy.viewSpecs}
                    </button>
                  </m.div>
                ))}
              </m.div>
            ) : (
              <m.div
                key="detail"
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 36 }}
                animate={{ opacity: 1, y: 0 }}
                exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 36 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="bg-white/[0.03] backdrop-blur-lg border border-white/10 shadow-2xl grid grid-cols-1 lg:grid-cols-2 overflow-hidden min-h-[850px] relative z-30"
              >
                <div className="relative h-[450px] lg:h-auto bg-[#080E1A] overflow-hidden border-r border-white/5">
              <Image
                src={selectedProduct.detailImage}
                alt={selectedProduct.name}
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                quality={65}
                className="absolute inset-0 w-full h-full object-cover object-center opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A111F] via-transparent to-transparent opacity-90" />
              <m.div
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: shouldReduceMotion ? 0 : 0.18 }}
                className="absolute bottom-12 left-12 text-white z-10"
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.5em] text-terracotta mb-3 font-montserrat">{copy.imageLabel}</p>
                <h4 className="text-3xl font-black font-montserrat uppercase tracking-tighter leading-none">
                  {copy.imageTitle}
                </h4>
              </m.div>
            </div>

            <div className="p-8 lg:p-16 flex flex-col bg-transparent">
              <div className="mb-12">
                <h3 className="text-4xl lg:text-5xl font-black font-montserrat text-white uppercase mb-4 leading-none tracking-tighter">
                  {selectedProduct.name}
                </h3>
                <div className="h-1.5 w-24 bg-terracotta mb-6" />
                <p className="text-white/70 text-[11px] font-bold uppercase tracking-[0.2em] font-montserrat">
                  {selectedProduct.tagline}
                </p>
              </div>

              <div className="flex border-b border-white/10 mb-10 overflow-x-auto no-scrollbar">
                {([
                  { id: "applications", label: copy.tabs.applications },
                  { id: "mechanism", label: copy.tabs.mechanism },
                  { id: "specs", label: copy.tabs.specs },
                ] as const).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setInfoTab(tab.id)}
                    className={`px-8 py-5 text-[10px] font-bold uppercase tracking-widest transition-all relative whitespace-nowrap font-montserrat ${
                      infoTab === tab.id ? "text-white" : "text-white/50 hover:text-white"
                    }`}
                  >
                    {tab.label}
                    {infoTab === tab.id && <span className="absolute bottom-0 left-0 right-0 h-1 bg-terracotta" />}
                  </button>
                ))}
              </div>

              <div className="flex-grow min-h-[400px]">
                <AnimatePresence mode="wait">
                  <m.div
                    key={infoTab}
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, x: 18 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, x: -18 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    {infoTab === "applications" && (
                      <div className="space-y-8">
                        {selectedProduct.applications.map((application, index) => (
                          <div key={`${application.title}-${index}`} className="flex gap-6 items-start">
                            <span className="text-terracotta font-black text-xl italic opacity-50 font-montserrat">
                              0{index + 1}
                            </span>
                            <div>
                              <h4 className="font-bold text-white/90 text-sm uppercase mb-2 tracking-tight font-montserrat">
                                {application.title}
                              </h4>
                              <p className="text-white/60 text-sm leading-relaxed max-w-md font-light">
                                {application.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {infoTab === "mechanism" && (
                      <div className="grid grid-cols-1 gap-4">
                        {selectedProduct.mechanism.map((step, index) => (
                          <div key={`${step.title}-${index}`} className="bg-white/5 p-6 border-l-4 border-terracotta/60 hover:border-terracotta transition-all">
                            <h5 className="text-white font-black text-[10px] uppercase mb-2 tracking-widest font-montserrat">
                              {step.title}
                            </h5>
                            <p className="text-white/70 text-sm leading-snug font-light">{step.description}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {infoTab === "specs" && (
                      <div className="divide-y divide-white/5 border border-white/10 rounded-sm">
                        {selectedProduct.properties.map((property, index) => (
                          <div key={`${property.label}-${index}`} className="flex justify-between items-center py-5 px-6 hover:bg-white/5 transition-colors gap-6">
                            <span className="text-[10px] font-bold text-terracotta uppercase tracking-widest font-montserrat">
                              {property.label}
                            </span>
                            <span className="text-sm font-bold text-white tracking-tight text-right">
                              {property.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </m.div>
                </AnimatePresence>
              </div>

              <div className="mt-12">
                <a
                  href="#contacto"
                  className="w-full bg-terracotta text-white h-16 font-bold text-[10px] uppercase tracking-[0.4em] hover:bg-white hover:text-petroleum transition-all shadow-2xl flex items-center justify-center gap-4 group cursor-pointer"
                >
                  {copy.requestQuote}
                  <span className="group-hover:translate-x-2 transition-transform">&rarr;</span>
                </a>
              </div>
            </div>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </LazyMotion>
  );
}
